// ═══════════════════════════════════════════════════════
// ULTIMAZE TOWER WARS — NotificationService (Singleton)
// ═══════════════════════════════════════════════════════

import type { INotificationService } from '../interfaces';

export class NotificationService {
  private static instance: NotificationService | null = null;
  private readonly adapter: INotificationService;

  private constructor(adapter: INotificationService) {
    this.adapter = adapter;
  }

  static init(adapter: INotificationService): NotificationService {
    NotificationService.instance = new NotificationService(adapter);
    return NotificationService.instance;
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      throw new Error(
        'NotificationService not initialised. Call NotificationService.init(adapter) first.'
      );
    }
    return NotificationService.instance;
  }

  /**
   * Schedules a daily pack reminder at the next UTC midnight after nowMs.
   * nextMidnight = Math.ceil(nowMs / 86_400_000) * 86_400_000
   */
  async scheduleDailyPackReminder(nowMs: number): Promise<void> {
    const nextMidnight = new Date(Math.ceil(nowMs / 86_400_000) * 86_400_000);
    await this.adapter.scheduleLocal({
      title: 'Dein Pack wartet!',
      body: 'Hol dir jetzt dein kostenloses Pack.',
      atDate: nextMidnight,
    });
  }

  /**
   * Schedules a quest reminder 1 hour from nowMs.
   */
  async scheduleQuestReminder(questTitle: string, nowMs: number): Promise<void> {
    const ONE_HOUR_MS = 60 * 60 * 1_000;
    const atDate = new Date(nowMs + ONE_HOUR_MS);
    await this.adapter.scheduleLocal({
      title: 'Quest bereit!',
      body: `${questTitle} — Belohnung einsammeln!`,
      atDate,
    });
  }

  /**
   * Schedules a match engagement reminder 23 hours from nowMs.
   * "Du wirst gesucht!" — encourages the player to return and play.
   * Cancel this on match start via cancelMatchReminder().
   */
  async scheduleMatchReminder(nowMs: number): Promise<void> {
    const TWENTY_THREE_HOURS_MS = 23 * 60 * 60 * 1_000;
    const atDate = new Date(nowMs + TWENTY_THREE_HOURS_MS);
    await this.adapter.scheduleLocal({
      title: 'Spieler suchen dich!',
      body: 'Andere Spieler warten auf einen Gegner.',
      atDate,
    });
  }

  /**
   * Cancels the match engagement reminder.
   * Call this when the player starts a match — they are already playing.
   */
  async cancelMatchReminder(): Promise<void> {
    await this.adapter.cancelAll().catch(() => {})
  }

  async cancelAllReminders(): Promise<void> {
    await this.adapter.cancelAll();
  }

  async requestPermissions(): Promise<boolean> {
    return this.adapter.requestPermission();
  }
}
